/**
 * @fileoverview Dashboard navigation
 * @description Light calm sidebar (desktop) and compact top brand/account chrome (mobile).
 * No bottom navigation bar.
 * @module components/layout/Sidebar
 */

import { Link, NavLink } from 'react-router-dom';
import {
  Users,
  LogOut,
  UserCircle,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { appVersionLabel } from '@/lib/version';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';

const navItems = [
  { to: '/', icon: Users, label: 'Clients', end: true },
  { to: '/invoices', icon: FileText, label: 'Invoices', end: false },
  { to: '/account', icon: UserCircle, label: 'Account', end: false },
];

function AccountMenu({
  showLabel = false,
  align = 'end',
  className,
}: {
  showLabel?: boolean;
  align?: 'start' | 'end' | 'center';
  className?: string;
}): React.JSX.Element {
  const { user, trainerProfile, signOut } = useAuth();
  const initials = user?.email?.slice(0, 2).toUpperCase() ?? 'TR';
  const role = trainerProfile?.professional_type?.replace('_', ' ') ?? 'Trainer';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'h-auto gap-2 px-2 py-1.5 text-left hover:bg-black/5',
            showLabel ? 'max-w-[14rem]' : '',
            className,
          )}
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-neutral-200 text-xs font-semibold text-neutral-800">
              {initials}
            </AvatarFallback>
          </Avatar>
          {showLabel && (
            <span className="hidden min-w-0 flex-col sm:flex">
              <span className="truncate text-xs font-medium text-neutral-900">
                {user?.email ?? 'Trainer'}
              </span>
              <span className="truncate text-[11px] capitalize text-neutral-500">{role}</span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user?.email}</p>
            <p className="text-xs capitalize leading-none text-muted-foreground">{role}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account">
            <UserCircle className="mr-2 h-4 w-4" />
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => void signOut()}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Sidebar(): React.JSX.Element {
  return (
    <aside className="relative hidden h-dvh w-[240px] shrink-0 flex-col border-r border-neutral-200 bg-[#f0efe9] lg:flex">
      <div className="border-b border-neutral-200 px-4 py-5">
        <Link to="/" className="block">
          <p className="heading-display text-2xl tracking-tight text-neutral-900">
            REPKEEP
          </p>
          <p className="text-xs text-neutral-500">Trainer workspace</p>
        </Link>
      </div>

      <nav className="flex flex-1 flex-col gap-0.5 px-3 py-4" aria-label="Primary">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition',
                isActive
                  ? 'bg-white font-medium text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:bg-white/70 hover:text-neutral-900',
              )
            }
          >
            <item.icon className="h-4 w-4 text-neutral-500" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-neutral-200 px-3 py-3">
        <AccountMenu showLabel className="w-full justify-start" align="start" />
        <p className="mt-2 px-2 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
          RepKeep v{appVersionLabel}
        </p>
      </div>
    </aside>
  );
}

/** Mobile-only top brand + Clients/Account links + account menu. */
export function MobileTopChrome(): React.JSX.Element {
  return (
    <header className="border-b border-neutral-200 bg-[#f0efe9] lg:hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="min-w-0">
          <p className="heading-display text-xl tracking-tight text-neutral-900">
            REPKEEP
          </p>
          <p className="text-[11px] text-neutral-500">Trainer workspace</p>
        </Link>
        <AccountMenu />
      </div>
      <nav
        className="flex gap-1 overflow-x-auto px-3 pb-3"
        aria-label="Primary"
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition',
                isActive
                  ? 'bg-white font-medium text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:bg-white/70',
              )
            }
          >
            <item.icon className="h-4 w-4 text-neutral-500" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
